import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchEvalAnalyticsOverview,
  type EvalAnalyticsOverview,
} from '@/admin/lib/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/admin/components/ui/chart';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/admin/components/ui/toggle-group';
import { Skeleton } from '@/admin/components/ui/skeleton';
import { ActivityHeatmap } from '@/admin/components/benchmark/activity-heatmap';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';

type TrendWindow = '7d' | '30d' | '90d';

function StatCard({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
}) {
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="text-2xl font-bold">{value}</div>
        {subtitle ? (
          <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function formatPercent(value?: number | null) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return `${Math.round(value * 100)}%`;
}

function formatDecimal(value?: number | null, digits = 1) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toFixed(digits);
}

function curveValue(
  curves: Record<string, number> | undefined,
  key: string
): number | undefined {
  const value = curves?.[key];
  return typeof value === 'number' && !Number.isNaN(value) ? value : undefined;
}

function extractCurveKs(keys: string[]) {
  const passAt = new Set<number>();
  const passHat = new Set<number>();

  for (const key of keys) {
    const match = key.match(/^pass_(at|hat)_(\d+)$/);
    if (!match) continue;
    const value = Number(match[2]);
    if (!Number.isFinite(value)) continue;
    if (match[1] === 'at') {
      passAt.add(value);
    } else {
      passHat.add(value);
    }
  }

  return Array.from(passAt)
    .filter(value => passHat.has(value))
    .sort((left, right) => left - right);
}

const trendChartConfig = {
  pass_at: {
    label: 'pass@k',
    color: 'var(--chart-1)',
  },
  pass_hat: {
    label: 'pass^k',
    color: 'var(--chart-2)',
  },
} satisfies ChartConfig;

export const BenchmarkDashboard: React.FC = () => {
  const [trendWindow, setTrendWindow] = useState<TrendWindow>('30d');
  const [overview, setOverview] = useState<EvalAnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedK, setSelectedK] = useState('1');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    fetchEvalAnalyticsOverview(trendWindow)
      .then(data => {
        if (!active) return;
        setOverview(data);
      })
      .catch(err => {
        if (!active) return;
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load evaluator analytics'
        );
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [trendWindow]);

  const availableKs = useMemo(
    () => extractCurveKs(overview?.available_curve_keys || []),
    [overview]
  );

  useEffect(() => {
    if (availableKs.length === 0) {
      setSelectedK('1');
      return;
    }
    if (!availableKs.includes(Number(selectedK))) {
      setSelectedK(String(availableKs[0]));
    }
  }, [availableKs, selectedK]);

  const passAtKey = `pass_at_${selectedK}`;
  const passHatKey = `pass_hat_${selectedK}`;

  const trendPoints = useMemo(() => {
    return (overview?.trend || []).map(point => ({
      date: point.bucket_start,
      experiment_count: point.experiment_count,
      pass_at:
        typeof curveValue(point.curve_averages, passAtKey) === 'number'
          ? curveValue(point.curve_averages, passAtKey)! * 100
          : null,
      pass_hat:
        typeof curveValue(point.curve_averages, passHatKey) === 'number'
          ? curveValue(point.curve_averages, passHatKey)! * 100
          : null,
    }));
  }, [overview, passAtKey, passHatKey]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return <div className="text-sm text-destructive">Error: {error}</div>;
  }

  const summary = overview?.summary;
  const suites = overview?.suites || [];
  const adapters = overview?.adapters || [];
  const evalModes = overview?.eval_modes || [];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
        <StatCard
          title="Experiments"
          value={summary?.total_experiments || 0}
          subtitle={`${summary?.total_suites || 0} suites`}
        />
        <StatCard
          title="Trials"
          value={summary?.total_trials || 0}
          subtitle={`${summary?.graded_trials || 0} graded`}
        />
        <StatCard
          title="Pass Rate"
          value={formatPercent(summary?.pass_rate)}
          subtitle={`${summary?.passing_trials || 0}/${summary?.graded_trials || 0} passing`}
        />
        <StatCard
          title="Avg Score"
          value={formatPercent(summary?.average_score)}
          subtitle={`avg parallelism ${formatDecimal(summary?.average_parallelism)}`}
        />
        <StatCard
          title={`Avg p@${selectedK}`}
          value={formatPercent(curveValue(summary?.curve_averages, passAtKey))}
          subtitle="Across experiments"
        />
        <StatCard
          title={`Avg p^${selectedK}`}
          value={formatPercent(curveValue(summary?.curve_averages, passHatKey))}
          subtitle={`${summary?.total_adapters || 0} adapters`}
        />

        <Card className="col-span-2 md:col-span-2 lg:col-span-6">
          <CardHeader className="p-4 pb-0">
            <CardTitle className="text-base">Activity</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-2">
            <ActivityHeatmap
              blockSize={20}
              data={(overview?.trend || []).map(point => ({
                date: point.bucket_start,
                value: point.experiment_count,
              }))}
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-7">
        <Card className="lg:col-span-4">
          <CardHeader className="flex flex-row items-center justify-between p-4 pb-2">
            <div>
              <CardTitle className="text-base">Pass Curve Trend</CardTitle>
              <p className="text-sm text-muted-foreground">
                Average experiment-level pass@k and pass^k by day
              </p>
            </div>
            <div className="flex items-center gap-2">
              {availableKs.length > 0 ? (
                <ToggleGroup
                  type="single"
                  value={selectedK}
                  onValueChange={value => {
                    if (value) setSelectedK(value);
                  }}
                  variant="outline"
                  size="sm"
                >
                  {availableKs.slice(0, 4).map(value => (
                    <ToggleGroupItem
                      key={value}
                      value={String(value)}
                      className="h-7 px-2 text-xs"
                    >
                      k={value}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              ) : null}
              <ToggleGroup
                type="single"
                value={trendWindow}
                onValueChange={value => {
                  if (value === '7d' || value === '30d' || value === '90d') {
                    setTrendWindow(value);
                  }
                }}
                variant="outline"
                size="sm"
              >
                <ToggleGroupItem value="90d" className="h-7 px-2 text-xs">
                  90d
                </ToggleGroupItem>
                <ToggleGroupItem value="30d" className="h-7 px-2 text-xs">
                  30d
                </ToggleGroupItem>
                <ToggleGroupItem value="7d" className="h-7 px-2 text-xs">
                  7d
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-[260px] w-full">
              <ChartContainer
                config={trendChartConfig}
                className="h-full w-full"
              >
                <LineChart data={trendPoints}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={32}
                    tickFormatter={value =>
                      new Date(value).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })
                    }
                  />
                  <YAxis
                    domain={[0, 100]}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickFormatter={value => `${value}%`}
                  />
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        labelFormatter={value =>
                          new Date(String(value)).toLocaleDateString()
                        }
                        formatter={(value, name, item) => {
                          if (name === 'pass_at') {
                            return (
                              <div className="flex w-full items-center justify-between gap-6">
                                <span>pass@{selectedK}</span>
                                <span>
                                  {formatPercent(Number(value) / 100)}
                                </span>
                              </div>
                            );
                          }
                          if (name === 'pass_hat') {
                            return (
                              <div className="flex w-full items-center justify-between gap-6">
                                <span>pass^{selectedK}</span>
                                <span>
                                  {formatPercent(Number(value) / 100)}
                                </span>
                              </div>
                            );
                          }
                          return (
                            <div className="flex w-full items-center justify-between gap-6">
                              <span>{String(name)}</span>
                              <span>{String(item?.value ?? value)}</span>
                            </div>
                          );
                        }}
                      />
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="pass_at"
                    stroke="var(--color-pass_at)"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="pass_hat"
                    stroke="var(--color-pass_hat)"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                </LineChart>
              </ChartContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-base">Top Suites</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-4">Suite</TableHead>
                  <TableHead className="text-right">Experiments</TableHead>
                  <TableHead className="pr-4 text-right">
                    p@{selectedK}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suites.slice(0, 8).map(suite => (
                  <TableRow key={suite.suite_ref}>
                    <TableCell className="pl-4">
                      <div className="font-medium">{suite.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {suite.suite_ref}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {suite.experiment_count}
                    </TableCell>
                    <TableCell className="pr-4 text-right">
                      {formatPercent(
                        curveValue(suite.curve_averages, passAtKey)
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {suites.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="py-8 text-center text-muted-foreground"
                    >
                      No suite data available
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-base">Eval Mode Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4">Mode</TableHead>
                <TableHead className="text-right">Experiments</TableHead>
                <TableHead className="text-right">Trials</TableHead>
                <TableHead className="text-right">Avg Score</TableHead>
                <TableHead className="text-right">p@{selectedK}</TableHead>
                <TableHead className="pr-4 text-right">p^{selectedK}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {evalModes.map(mode => (
                <TableRow key={mode.eval_mode}>
                  <TableCell className="pl-4">
                    <div className="font-medium">{mode.eval_mode}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatPercent(mode.pass_rate)} pass rate
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {mode.experiment_count}
                  </TableCell>
                  <TableCell className="text-right">
                    {mode.trial_count}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatPercent(mode.average_score)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatPercent(curveValue(mode.curve_averages, passAtKey))}
                  </TableCell>
                  <TableCell className="pr-4 text-right">
                    {formatPercent(curveValue(mode.curve_averages, passHatKey))}
                  </TableCell>
                </TableRow>
              ))}
              {evalModes.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-8 text-center text-muted-foreground"
                  >
                    No eval mode data available
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-base">Adapter Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4">Adapter</TableHead>
                <TableHead className="text-right">Experiments</TableHead>
                <TableHead className="text-right">Trials</TableHead>
                <TableHead className="text-right">Avg Score</TableHead>
                <TableHead className="text-right">p@{selectedK}</TableHead>
                <TableHead className="pr-4 text-right">p^{selectedK}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {adapters.map(adapter => (
                <TableRow key={adapter.adapter}>
                  <TableCell className="pl-4">
                    <div className="font-medium">{adapter.adapter}</div>
                    <div className="text-xs text-muted-foreground">
                      {adapter.environment_modes?.join(', ') || '—'}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {adapter.experiment_count}
                  </TableCell>
                  <TableCell className="text-right">
                    {adapter.trial_count}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatPercent(adapter.average_score)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatPercent(
                      curveValue(adapter.curve_averages, passAtKey)
                    )}
                  </TableCell>
                  <TableCell className="pr-4 text-right">
                    {formatPercent(
                      curveValue(adapter.curve_averages, passHatKey)
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {adapters.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-8 text-center text-muted-foreground"
                  >
                    No adapter data available
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
